import type { SupWeb3VaultBalance } from "@/types";
import React, { useEffect, useMemo } from "react";

import Web3DebugState from "@/components/Web3/Debug/State";
import Web3CommonCartDisabled from "@/components/Web3/Common/Cart/Disabled";
import useGetAdminConfig from "@/hooks/api/useGetAdminConfig";
import useTokenSuperpositionsBalances from "@/hooks/api/useTokenSuperpositionsBalances";
import Web3CommonCartLoader from "@/components/Web3/Common/Cart/Loader";
import Web3Withdraw from "@/components/Web3/Withdraw";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";

export default function LayoutSidebarModeWithdraw({ className }: { className?: string }) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();
  const { data: adminConfig } = useGetAdminConfig();

  const isDisabled = useMemo(() => {
    return adminConfig?.isWithdrawalDisabled;
  }, [adminConfig]);

  const { data, isLoading } = useTokenSuperpositionsBalances("fully-cached");

  const sourceChainId = useMemo(() => storedData.withdrawalChainId, [storedData.withdrawalChainId]);

  const superPositions = useMemo(() => {
    return (
      data?.superPositions.filter(
        vault =>
          vault.chain_id === sourceChainId &&
          storedData.withdrawCart.find(item => item.vault_id === vault.vault.id && item.is_erc20 === vault.is_erc20 && item.superform_id === vault.superposition_id) !== undefined
      ) || ([] as SupWeb3VaultBalance[])
    );
  }, [data, storedData.withdrawCart, sourceChainId]);

  // Clear the cart if the withdrawal vault doesn't match superpositions
  useEffect(() => {
    if (storedData.withdrawCart.length > 0 && superPositions.length === 0 && data !== undefined) {
      storedDispatch({ type: "clearWithdrawCart" });
    }
  }, [data, storedData.withdrawCart.length, storedDispatch, superPositions]);

  return !data && isLoading ? (
    <Web3CommonCartLoader />
  ) : isDisabled ? (
    <Web3CommonCartDisabled type="withdraw" />
  ) : (
    <Web3DebugState type="withdraw">
      <Web3Withdraw className={className} superPositions={superPositions} sourceChainId={sourceChainId as number} />
    </Web3DebugState>
  );
}
